import React, { useState } from 'react';
import MarkdownFieldSimple from '../MarkdownFieldSimple';
import CommentContent from './CommentContent';
import { formatDistanceToNow } from 'date-fns';
import './CommentContent.css';

const CommentThread = ({ 
  comment, 
  onReply, 
  onEdit, 
  onDelete,
  maxDepth = 3,
  currentDepth = 0,
  printMode = false,
  user // Receive user as prop instead of useAuth
}) => {
  const [isReplying, setIsReplying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [editContent, setEditContent] = useState(comment.content);
  const [submitting, setSubmitting] = useState(false);

  const canEdit = comment.author?._id === user?._id && !comment.deleted;
  const canDelete = comment.author?._id === user?._id || user?.role === 'admin';
  const canReply = currentDepth < maxDepth && !printMode;

  // Temporary debug logging
  if (comment && user) {
    console.log('DEBUG - Comment author ID:', comment.author?._id);
    console.log('DEBUG - Current user ID:', user?._id);
    console.log('DEBUG - Can delete:', canDelete);
  }

  // Handle reply submission
  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;
    
    try {
      setSubmitting(true);
      await onReply(comment._id, replyContent);
      setReplyContent('');
      setIsReplying(false);
    } catch (err) {
      console.error('Error submitting reply:', err);
      alert('Failed to post reply. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit submission
  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!editContent.trim()) return;
    
    try {
      setSubmitting(true);
      await onEdit(comment._id, editContent);
      setIsEditing(false);
    } catch (err) {
      console.error('Error submitting edit:', err);
      alert('Failed to edit comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Format timestamp
  const formatTimestamp = (date) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch {
      return 'recently';
    }
  };

  // Render deleted comment
  if (comment.deleted && !printMode) {
    return (
      <div className={`comment-thread deleted depth-${currentDepth}`}>
        <div className="comment-content">
          <p className="deleted-message">[Comment deleted]</p>
        </div>
        {comment.replies && comment.replies.length > 0 && (
          <div className="comment-replies">
            {comment.replies.map(reply => (
              <CommentThread
                key={reply._id}
                comment={reply}
                onReply={onReply}
                onEdit={onEdit}
                onDelete={onDelete}
                maxDepth={maxDepth}
                currentDepth={currentDepth + 1}
                printMode={printMode}
                user={user}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`comment-thread depth-${currentDepth} ${printMode ? 'print-mode' : ''}`}>
      <div className="comment-header">
        {comment.author?.avatar && (
          <img 
            src={comment.author.avatar} 
            alt={comment.author.username}
            className="comment-avatar"
          />
        )}
        <div className="comment-meta">
          <span className="comment-author">{comment.author?.username || 'Unknown'}</span>
          <span className="comment-timestamp" title={new Date(comment.created_at).toLocaleString()}>
            {formatTimestamp(comment.created_at)}
          </span>
          {comment.edited && (
            <span className="comment-edited" title={`Edited ${formatTimestamp(comment.edited_at)}`}>
              (edited)
            </span>
          )}
        </div>
      </div>

      <div className="comment-body">
        {isEditing ? (
          <form onSubmit={handleSubmitEdit} className="edit-form">
            <MarkdownFieldSimple
              type="textarea"
              rows={3}
              value={editContent}
              onChange={setEditContent}
              placeholder="Edit your comment..."
              disabled={submitting}
            />
            <div className="edit-actions">
              <button 
                type="submit" 
                disabled={!editContent.trim() || submitting}
                className="btn-primary btn-sm"
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
              <button 
                type="button" 
                onClick={() => {
                  setIsEditing(false);
                  setEditContent(comment.content);
                }}
                disabled={submitting}
                className="btn-secondary btn-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="comment-content">
            <CommentContent 
              content={comment.content} 
              mentions={comment.mentions || []}
            />
          </div>
        )}
      </div>

      {/* Actions - not shown in print mode */}
      {!printMode && (
        <div className="comment-actions">
          {canReply && (
            <button 
              onClick={() => setIsReplying(!isReplying)}
              className="btn-link"
            >
              Reply
            </button>
          )}
          {canEdit && !isEditing && (
            <button 
              onClick={() => setIsEditing(true)}
              className="btn-link"
            >
              Edit
            </button>
          )}
          {canDelete && (
            <button 
              onClick={() => onDelete(comment._id)}
              className="btn-link danger"
            >
              Delete
            </button>
          )}
        </div>
      )}

      {/* Reply form */}
      {isReplying && !printMode && (
        <form onSubmit={handleSubmitReply} className="reply-form">
          <MarkdownFieldSimple
            type="textarea"
            rows={3}
            value={replyContent}
            onChange={setReplyContent}
            placeholder={`Reply to ${comment.author?.username}...`}
            disabled={submitting}
          />
          <div className="reply-actions">
            <button 
              type="submit" 
              disabled={!replyContent.trim() || submitting}
              className="btn-primary btn-sm"
            >
              {submitting ? 'Posting...' : 'Post Reply'}
            </button>
            <button 
              type="button" 
              onClick={() => {
                setIsReplying(false);
                setReplyContent('');
              }}
              disabled={submitting}
              className="btn-secondary btn-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Nested replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="comment-replies">
          {comment.replies.map(reply => (
            <CommentThread
              key={reply._id}
              comment={reply}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              maxDepth={maxDepth}
              currentDepth={currentDepth + 1}
              printMode={printMode}
              user={user}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentThread;